import React from 'react';

/**
 * [[include:components/disclaimer-reference.md]]
 * @category Components
 */
export type DisclaimerReferenceComponent =
  React.ComponentType<DisclaimerReferenceProps>;

export enum DisclaimerKind {
  /**
   * This disclaimer is rendered within the App, using the passed in
   * [[DisclaimerComponent]].
   */
  AppRendered,
  /**
   * The referenced disclaimer text is displayed using an external system, e.g.
   * via the page footer (by default), or in a different manner. Make sure to
   * set `externallyRenderedDisclaimerKind="module-reference"` on the Feature
   * App, if you _don't_ want to have them show up in a footer.
   */
  ExternallyRendered,
}

/**
 * Show a (perhaps interactive) reference for the disclaimer text.
 *
 * The component should have `display: inline` or equivalent, to be able to
 * be rendered inbetween other text.
 *
 * @category Component Props
 */
export interface DisclaimerReferenceProps {
  /** See [[DisclaimerKind]] */
  readonly kind: DisclaimerKind;

  /**
   * The disclaimer's scope that was used for registering with the disclaimer
   * manager. For our feature apps, we only add a scope when showing a layer.
   *
   * Currently, there are no disclaimers of kind `module-reference` in a layer /
   * which have scope.
   */
  readonly scope: string | undefined;

  /**
   * The reference text for this disclaimer / footnote, as returned from the
   * GFA disclaimer manager
   */
  readonly reference: string;

  /**
   * The disclaimer text to which this is a reference, as returned from the
   * GFA disclaimer manager
   */
  readonly text: string;

  /**
   * An aria label that can be used for the reference. The corresponding
   * translation is `global.disclaimerReferenceLabel` in all apps.
   */
  readonly ariaLabel: string;

  /**
   * If the disclaimer is opened inside a layer, this label can be used on the
   * close button of the layer
   */
  readonly closeButtonAriaLabel: string;

  /**
   * The `id` prop that is passed to the respective [[DisclaimerComponent]].
   *
   * Could be used for interactivity.
   *
   * This will always be set for `kind: DisclaimerKind.AppRendered`, but note
   * that the id will only point to e.g. a rendered HTML element, if the
   * [[DisclaimerComponent]] implements this using its `id` prop.
   */
  readonly disclaimerTextId?: string;
}
